import { useState } from "react";
import { consumeMorphCandidate, morphNameFor } from "../../utils/productMorph";
import "./ProductGallery.css";
import { imageProps, SIZES } from "../../utils/imageUrl";

const MAX_IMAGE_SLOTS = 5;

// Converts a YouTube/Vimeo link to its embeddable form; returns null for
// anything else (treated as a direct video file URL, played via <video>).
const toEmbedUrl = (url) => {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
};

const ProductGallery = ({ images = [], videoUrl = null, productName = "", productId = null }) => {
  const [active, setActive] = useState(0);
  // Read-and-clear exactly once, via the lazy useState initializer — never
  // re-consumed on later re-renders, so a thumbnail click afterwards can't
  // accidentally re-trigger it. Only applies if this is the SAME product
  // the click on the card came from (arriving via direct URL/back button
  // leaves this null — no name applied, nothing to clash with).
  const [morphProductId] = useState(() => consumeMorphCandidate());
  const isMorphTarget = productId != null && productId === morphProductId;

  // Only real slots — no padding to a fixed count, no disabled placeholders.
  const slots = [
    ...images.slice(0, MAX_IMAGE_SLOTS).filter(Boolean).map((src) => ({ type: "image", src })),
    ...(videoUrl ? [{ type: "video", src: videoUrl, embedUrl: toEmbedUrl(videoUrl) }] : []),
  ];
  const current = slots[active] || null;

  return (
    <div className="pg-wrap">
      {/* Main display */}
      <div className="pg-main">
        {!current ? (
          <div className="pg-main-placeholder" aria-label="No image available">
            <span className="pg-placeholder-icon" aria-hidden="true">🖼</span>
            <span className="pg-placeholder-label">No image</span>
          </div>
        ) : current.type === "video" ? (
          current.embedUrl ? (
            <iframe
              className="pg-main-video"
              src={current.embedUrl}
              title={`${productName} video`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video className="pg-main-video" src={current.src} controls />
          )
        ) : (
          // Every image slot is mounted at once (stacked, opacity-toggled)
          // so switching the active one crossfades — both the outgoing and
          // incoming image transition simultaneously since they're both
          // already in the DOM. Same mechanism as ShopByCategory's slide
          // crossfade, but on the motion-token timing (this is a deliberate
          // click, not slow ambient rotation). Video slots aren't part of
          // this stack — switching into/out of one stays an instant swap.
          slots.map((s, i) => s.type === "image" && (
            <img
              key={s.src}
              className={`pg-main-img${i === active ? " is-active" : ""}`}
              {...imageProps(s.src, "product", SIZES.gallery)}
              alt={productName}
              loading={i === 0 ? "eager" : "lazy"}
              decoding={i === 0 ? "sync" : "async"}
              style={
                isMorphTarget && i === 0
                  ? { viewTransitionName: morphNameFor(productId), contain: "paint" }
                  : undefined
              }
            />
          ))
        )}
      </div>

      {/* Thumbnail row — one button per real image/video, nothing else */}
      {slots.length > 0 && (
        <div className="pg-thumbs" role="list" aria-label="Product image and video thumbnails">
          {slots.map((slot, i) => (
            <button
              key={i}
              type="button"
              role="listitem"
              className={`pg-thumb${active === i ? " pg-thumb-active" : ""}`}
              aria-label={slot.type === "video" ? "Play product video" : `View image ${i + 1}`}
              onClick={() => setActive(i)}
            >
              {slot.type === "video" ? (
                <span className="pg-thumb-video" aria-hidden="true">▶</span>
              ) : (
                <img {...imageProps(slot.src, "product", SIZES.thumb)} alt={`${productName} view ${i + 1}`} loading="lazy" decoding="async" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
